import React, { useState } from 'react';
import { FlaskConical, Zap, Cpu, Binary, Target, ArrowRight } from 'lucide-react';
import { getText } from '../data/content';
import { theme, combineClasses, getTextColor, getCardStyle } from '../theme/theme';
import { researchContent } from '../data/content-research';
import Collapsible from '../components/Collapsible';

const withBase = (p) => {
  if (!p) return p;
  return `${import.meta.env.BASE_URL}${String(p).replace(/^\//, '')}`;
};

/**
 * ============================================================================
 * 页面：研究方法 - Research.jsx
 * ============================================================================
 *
 * 【作用】展示四个研究方向（合成/表征/理论与计算/AI 加速）：左侧 Tab 切换，右侧显示当前方向的标题、图片、描述、细节列表、设施列表。
 * 【如何修改】内容在 src/data/content-research.js 的 topics 数组；每个 topic 含 title、subtitle、color、image、content、details、facilities、link；新增设施在对应 topic.facilities 中追加对象。
 *
 * 【参数说明】props: lang, themeMode。内部状态：activeTab（当前选中的方向索引 0–3）。icons/colors 与 topics 顺序对应。
 */
const ResearchView = ({ lang, themeMode = "dark" }) => {
  const isLight = themeMode === "light";
  const [activeTab, setActiveTab] = useState(0);
  // 图标/配色顺序需与 src/data/content-research.js 的 topics 顺序一致
  const icons = [FlaskConical, Zap, Cpu, Binary]; // Synthesis, Characterization, Theory&Computation, AI
  const colors = [
    theme.colors.gradients.warm,
    theme.colors.gradients.cool,
    theme.colors.gradients.deep,
    theme.colors.gradients.fresh
  ];

  // Define currentTopic once at the top of the render, so it is available throughout the component
  const currentTopic = researchContent?.topics?.[activeTab];

  // Safe fallbacks
  const currentDetails = Array.isArray(currentTopic?.details) ? currentTopic.details : [];
  const currentFacilities = Array.isArray(currentTopic?.facilities) ? currentTopic.facilities : [];
  const currentImage = currentTopic?.image ? withBase(currentTopic.image) : null;
  const currentLink = currentTopic?.link ? withBase(currentTopic.link) : null;

  return (
    <div className={combineClasses(
      "min-h-screen pt-8 pb-20 px-4 md:px-8",
      theme.layout.container,
      theme.animations.fadeIn
    )}>
      <div className="flex flex-col lg:flex-row gap-12 h-full">
        {/* Left Sidebar: 导航栏 */}
        <div className="lg:w-1/3 space-y-4">
          <div className="mb-8">
            <h2 className={combineClasses(
              "text-4xl font-bold mb-2",
              isLight ? "text-gray-900" : "text-white"
            )}>{getText('research.title', lang)}</h2>
            <p className={combineClasses(
              "text-sm",
              isLight ? "text-gray-600" : "text-gray-400"
            )}>{getText('research.subtitle', lang)}</p>
          </div>

          {researchContent.topics.map((topic, index) => {
            const Icon = icons[index];
            return (
              <button
                key={index}
                onClick={() => setActiveTab(index)}
                className={combineClasses(
                  "w-full text-left p-6 rounded-xl border transition-all duration-300 flex items-center gap-4 group",
                  activeTab === index
                    ? `bg-gradient-to-r ${colors[index]} border-transparent shadow-lg transform scale-105`
                    : isLight 
                      ? "bg-gray-100 border-gray-200 hover:bg-gray-200 hover:border-gray-300"
                      : "bg-white/5 border-white/10 hover:bg-white/10 hover:border-white/30"
                )}
              >
                <div
                  className={combineClasses(
                    "p-3 rounded-lg transition-colors",
                    activeTab === index
                      ? isLight 
                        ? "text-white bg-gray-900" 
                        : "text-white bg-black/30"
                      : isLight
                        ? "bg-gray-200 text-gray-600 group-hover:text-gray-900"
                        : "bg-black/30 text-gray-400 group-hover:text-white"
                  )}
                >
                  <Icon className="w-6 h-6" />
                </div>
                <div>
                  <h3
                    className={combineClasses(
                      "font-bold text-lg",
                      activeTab === index 
                        ? "text-white" 
                        : isLight 
                          ? "text-gray-900" 
                          : "text-gray-300"
                    )}
                  >
                    {getText(`research.topics.${index}.title`, lang)}
                  </h3>
                  <p
                    className={combineClasses(
                      "text-xs",
                      activeTab === index 
                        ? "text-white/80" 
                        : isLight 
                          ? "text-gray-600" 
                          : "text-gray-500"
                    )}
                  >
                    {getText(`research.topics.${index}.subtitle`, lang)}
                  </p>
                </div>
              </button>
            );
          })}
        </div>

        {/* Right Content: 详细内容展示区 */}
        <div className={combineClasses(
          "lg:w-2/3 backdrop-blur-xl rounded-3xl p-8 md:p-12 relative overflow-hidden border",
          isLight 
            ? "bg-gray-50 border-gray-200" 
            : "bg-black/40 border-white/10"
        )}>
          {/* 背景光晕动画 */}
          <div
            className={`absolute top-0 right-0 w-96 h-96 bg-gradient-to-br ${colors[activeTab]} opacity-20 blur-[100px] rounded-full -mr-20 -mt-20 transition-all duration-700`}
          ></div>

          <div className="relative z-10 h-full flex flex-col justify-center">
            <div className={combineClasses(
              "flex items-center gap-4 mb-6 font-mono text-sm uppercase tracking-widest",
              isLight ? "text-gray-600" : "text-gray-400"
            )}>
              <span className={combineClasses(
                "w-12 h-[1px]",
                isLight ? "bg-gray-400" : "bg-gray-600"
              )}></span>
              Step {activeTab + 1}
            </div>

            <h2 className={combineClasses(
              "text-4xl md:text-5xl font-bold mb-6 leading-tight",
              isLight ? "text-gray-900" : "text-white"
            )}>
              {getText(`research.topics.${activeTab}.subtitle`, lang)}
            </h2>

            {/* 研究方向图片 */}
            {currentImage ? (
              <div className={combineClasses(
                "mb-6 rounded-xl overflow-hidden border",
                isLight ? "border-gray-200" : "border-white/10"
              )}>
                <img
                  src={currentImage}
                  alt={getText(`research.topics.${activeTab}.title`, lang)}
                  className="w-full h-auto object-middle"
                  onError={(e) => {
                    // 图片加载失败时的处理：此处可填写隐藏或替换逻辑（如 e.currentTarget.style.display = 'none' 或替换为占位图）
                  }}
                />
              </div>
            ) : null}

            {/* 内容：首段 + 三个小节为下拉列表，格式不变（红/蓝/绿标题 + 列表） */}
            <div className={combineClasses(
              "text-lg md:text-xl leading-relaxed mb-6 border-l-2 pl-6",
              isLight ? "text-gray-700 border-gray-300" : "text-gray-300 border-white/20"
            )}>
              {currentTopic?.contentIntro?.[lang] ?? null}
            </div>
            {Array.isArray(currentTopic?.contentSections) && currentTopic.contentSections.length > 0 && (
              <div className="space-y-3 mb-10">
                {currentTopic.contentSections.map((section, idx) => (
                  <Collapsible
                    key={idx}
                    label={section?.title?.[lang] ?? section?.title?.en ?? section?.title?.cn ?? ''}
                    labelClassName={['text-red-400', 'text-blue-400', 'text-green-400'][idx]}
                    isLight={isLight}
                    defaultOpen={false}
                  >
                    <div className={combineClasses(
                      "text-base leading-relaxed",
                      isLight ? "text-gray-700" : "text-gray-300"
                    )}>
                      {section?.body?.[lang] ?? section?.body?.en ?? section?.body?.cn ?? null}
                    </div>
                  </Collapsible>
                ))}
              </div>
            )}

            {/* 研究方向细节 */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
              {currentDetails.map((detail, i) => {
                const text = detail?.[lang] ?? detail?.en ?? detail?.cn ?? '';
                return (
                  <div
                    key={i}
                    className={combineClasses(
                      "flex items-center gap-3 p-4 rounded-lg border transition-all",
                      isLight 
                        ? "bg-gray-50 border-gray-200 hover:border-gray-300" 
                        : "bg-white/5 border-white/10 hover:border-white/20"
                    )}
                  >
                    <Target className="w-5 h-5 text-cyan-400" />
                    <span className={combineClasses(
                      "font-medium",
                      isLight ? "text-gray-900" : "text-gray-200"
                    )}>
                      {text}
                    </span>
                  </div>
                );
              })}
            </div>

            {/* 设施和设备展示 */}
            {currentFacilities.length > 0 ? (
              <div className={combineClasses(
                "mt-8 pt-8 border-t",
                isLight ? "border-gray-200" : "border-white/10"
              )}>
                <h3 className={combineClasses(
                  "text-xl font-bold mb-4",
                  isLight ? "text-gray-900" : "text-white"
                )}>
                  {lang === 'cn' ? '关键设施与设备' : 'Key Facilities & Instruments'}
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {currentFacilities.map((facility, i) => {
                    const name = facility?.name?.[lang] ?? facility?.name?.en ?? facility?.name?.cn ?? '';
                    const desc = facility?.description?.[lang] ?? facility?.description?.en ?? facility?.description?.cn ?? '';
                    const img = facility?.image ? withBase(facility.image) : null;

                    return (
                      <div
                        key={i}
                        className={combineClasses(
                          "rounded-lg overflow-hidden hover:border-cyan-500/30 transition-all border",
                          isLight 
                            ? "bg-gray-50 border-gray-200" 
                            : "bg-white/5 border-white/10"
                        )}
                      >
                        {img ? (
                          <div className="h-56 overflow-hidden">
                            <img
                              src={img}
                              alt={name}
                              className="w-full h-auto object-middle"
                              onError={(e) => {
                                // 设施图片加载失败时的处理：此处可填写隐藏或替换逻辑
                              }}
                            />
                          </div>
                        ) : null}

                        <div className="p-4">
                          <h4 className={combineClasses(
                            "font-bold mb-2",
                            isLight ? "text-gray-900" : "text-white"
                          )}>
                            {name}
                          </h4>
                          <p className={combineClasses(
                            "text-sm",
                            isLight ? "text-gray-700" : "text-gray-300"
                          )}>
                            {desc}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : null}

            {/* 链接到详情页（如果有） */}
            {currentLink ? (
              <div className={combineClasses(
                "mt-6 pt-6 border-t",
                isLight ? "border-gray-200" : "border-white/10"
              )}>
                <a
                  href={currentLink}
                  className={combineClasses(
                    "inline-flex items-center gap-2 transition-colors font-semibold",
                    isLight 
                      ? "text-cyan-600 hover:text-gray-900" 
                      : "text-cyan-400 hover:text-white"
                  )}
                >
                  {lang === 'cn' ? '查看详细信息 →' : 'Learn More →'}
                  <ArrowRight className="w-4 h-4" />
                </a>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResearchView;